import * as React from 'react';
import { useSelector } from 'react-redux';
import { Theme, Typography, Box } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { OrderProduct, OrderProductWithUnits, SetProduct, DinCollectionMethod } from 'common/types';
import { getProductNameWithVariant } from 'common/utils/productUtils';
import {
	isLiftTicketProduct as _isLiftTicketProduct,
	productIsExistingSkipass,
	isKeycardProduct,
} from 'common/utils/liftTicketUtils';
import { getTranslation } from 'common/modules/translations';
import * as ViewSelectors from 'selectors/ViewSelectors';
import { getSkidataSegmentTranslation } from 'common/utils/segments';
import { useTranslation } from 'services/localization/useTranslation';
import { USER_DIDNT_INPUT_KEYCARD } from 'common/constants/skidataErrors';
import { PhotoUploadedType, PhotoUploaded, ticketHasPhotoRequired } from 'common/modules/skidata';

interface OwnProps {
	product: OrderProduct | OrderProductWithUnits | SetProduct;
	allowInteractionForItem: boolean;
	units: number;
	dinCollectionMethod: DinCollectionMethod;
	photoUploaded?: PhotoUploadedType;
}

type Props = OwnProps & WithStyles<typeof styles>;

export const ProductName = (props: Props) => {
	const { classes, product, allowInteractionForItem, units, photoUploaded } = props;
	const language = useSelector(ViewSelectors.lang);
	const { t } = useTranslation();

	const isLiftTicketProduct = (
		product: OrderProduct | OrderProductWithUnits | SetProduct,
	): product is OrderProduct => _isLiftTicketProduct(product);

	const shouldRenderPhotoUploadStatus =
		isLiftTicketProduct(product) &&
		(photoUploaded || (ticketHasPhotoRequired(product) && !photoUploaded)) &&
		!isKeycardProduct(product);

	const renderKeycardNumber = (product: OrderProduct) => (
		<Box mt={1} mr={1}>
			{product.liftKeycardNumber && product.liftKeycardNumber !== USER_DIDNT_INPUT_KEYCARD ? (
				<Box mt={1}>
					<Typography variant="caption" noWrap className={classes.keycardNumberItem}>
						{product.liftKeycardNumber}
					</Typography>
				</Box>
			) : (
				<Box mt={1}>
					<Typography variant="caption" color="error" noWrap className={classes.keycardNumberItem}>
						{t('completed.missingKeycards', 'Missing keycard ID(s)')}
					</Typography>
				</Box>
			)}
		</Box>
	);

	const renderPhotoUploadedStatus = () => (
		<Box mt={1}>
			<Box mt={1}>
				<Typography
					variant="caption"
					color={photoUploaded === PhotoUploaded.UPLOADED ? 'inherit' : 'error'}
					noWrap
					className={classes.keycardNumberItem}
				>
					{photoUploaded === PhotoUploaded.UPLOADED
						? t('shopperProducts.photoUploaded', 'Photo uploaded')
						: photoUploaded === PhotoUploaded.ERROR
						? t('shopperProducts.photoUploadedError', 'Photo upload failed')
						: t('shopperProducts.photoUploadedMissing', 'Missing photo')}
				</Typography>
			</Box>
		</Box>
	);

	return (
		<div>
			<Typography
				variant={'subtitle1'}
				className={!allowInteractionForItem ? classes.removedSetProduct : undefined}
			>
				{units > 1 ? `${units}x ` : ''}
				{getProductNameWithVariant(
					getTranslation(product.name, language),
					product.variant || null,
					language,
					undefined,
				)}
				{isLiftTicketProduct(product) &&
					` (${getSkidataSegmentTranslation(product.externalSegment, language)})`}
			</Typography>
			{isLiftTicketProduct(product) && (
				<Box className={classes.tags}>
					{productIsExistingSkipass(product) && renderKeycardNumber(product)}
					{shouldRenderPhotoUploadStatus && renderPhotoUploadedStatus()}
				</Box>
			)}
		</div>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		warningText: {
			lineHeight: 1.3,
		},
		removedSetProduct: {
			color: theme.palette.text.secondary,
		},
		keycardNumberItem: {
			padding: theme.spacing(0.75, 1),
			backgroundColor: theme.palette.background.secondary,
			borderRadius: 3,
		},
		existingKeycardLabel: {
			fontWeight: 500,
		},
		tags: {
			display: 'flex',
		},
	});

export default withStyles(styles)(ProductName);
